/**
 * Field notes modal — replaces the broken Voice action tile.
 *
 * Two affordances:
 *   1. Dictation via the browser's SpeechRecognition API. The transcribed
 *      text streams into the same textarea the typed-input mode uses, so
 *      a partial dictation can be edited or extended by typing without
 *      losing what's been said. We do NOT upload audio — Spire desktop
 *      doesn't play back audio attachments cleanly. The audio stream is
 *      discarded once transcription completes.
 *   2. Typed input as a fallback when SpeechRecognition isn't available
 *      (older Android browsers, locked-down Safari) or when the tech
 *      prefers it.
 *
 * On save the text POSTs to /api/v1/sales-orders/:id/notes which creates
 * a CRM note in Spire with subject 'Tinsmith field note'. Visible in the
 * Spire desktop UI Notes tab alongside signatures and photos.
 */

import { useEffect, useRef, useState } from 'react';
import { Mic, MicOff, Type, X } from 'lucide-react';
import { Button } from '../ui/button.js';
import { Feedback } from '../ui/feedback.js';
import { cn } from '../../lib/cn.js';
import { useDialogA11y } from '../../lib/use-dialog-a11y.js';

interface NotesModalProps {
  open: boolean;
  onClose: () => void;
  /** Called when the user taps Save. The modal closes after the callback
   * resolves; failure handling is the caller's responsibility (so the
   * existing parent-side mutation surfaces errors in one place). */
  onSave: (text: string) => Promise<void> | void;
  /** True while the save mutation is in flight; disables the Save button. */
  saving?: boolean;
  /** Error message from the most recent save attempt. */
  error?: string | null;
}

type SpeechState = 'idle' | 'starting' | 'listening' | 'unavailable';

// SpeechRecognition is webkit-prefixed on Safari and standard on Chromium.
// Cast through unknown because TypeScript's lib.dom doesn't ship the type.
function getRecognitionCtor():
  | (new () => SpeechRecognitionInstance)
  | null {
  if (typeof window === 'undefined') return null;
  const w = window as unknown as {
    SpeechRecognition?: new () => SpeechRecognitionInstance;
    webkitSpeechRecognition?: new () => SpeechRecognitionInstance;
  };
  return w.SpeechRecognition ?? w.webkitSpeechRecognition ?? null;
}

interface SpeechRecognitionInstance {
  continuous: boolean;
  interimResults: boolean;
  lang: string;
  start(): void;
  stop(): void;
  onresult: (e: SpeechRecognitionEventLike) => void;
  onerror: (e: { error: string; message?: string }) => void;
  onend: () => void;
}

interface SpeechRecognitionEventLike {
  resultIndex: number;
  results: {
    length: number;
    [i: number]: {
      isFinal: boolean;
      [j: number]: { transcript: string };
    };
  };
}

export function NotesModal({ open, onClose, onSave, saving, error }: NotesModalProps) {
  const [text, setText] = useState('');
  const [speech, setSpeech] = useState<SpeechState>('idle');
  const [speechErr, setSpeechErr] = useState<string | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const recognitionRef = useRef<SpeechRecognitionInstance | null>(null);
  /** Tracks the text that was committed by speech so we don't duplicate
   * interim chunks. The textarea content is `committedText + currentInterim`. */
  const committedRef = useRef('');

  // Reset when reopened.
  useEffect(() => {
    if (open) {
      setText('');
      setSpeech('idle');
      setSpeechErr(null);
      committedRef.current = '';
      // Focus the textarea so a typing user can start immediately.
      setTimeout(() => textareaRef.current?.focus(), 50);
    } else {
      stopRecognition();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Stop any active recognition on unmount.
  useEffect(() => {
    return () => stopRecognition();
  }, []);

  function stopRecognition() {
    const rec = recognitionRef.current;
    if (rec) {
      try {
        rec.stop();
      } catch {
        /* already stopped */
      }
    }
    recognitionRef.current = null;
  }

  function startDictation() {
    setSpeechErr(null);
    const Ctor = getRecognitionCtor();
    if (!Ctor) {
      setSpeech('unavailable');
      setSpeechErr('Dictation isn\'t supported in this browser. Type your note instead.');
      return;
    }
    setSpeech('starting');
    const rec = new Ctor();
    rec.continuous = true;
    rec.interimResults = true;
    rec.lang = navigator.language || 'en-US';
    committedRef.current = text;
    rec.onresult = (e: SpeechRecognitionEventLike) => {
      let interim = '';
      for (let i = e.resultIndex; i < e.results.length; i++) {
        const r = e.results[i];
        if (!r) continue;
        const transcript = r[0]?.transcript ?? '';
        if (r.isFinal) {
          committedRef.current = (committedRef.current + ' ' + transcript).trim();
        } else {
          interim += transcript;
        }
      }
      setText((committedRef.current + ' ' + interim).trim());
    };
    rec.onerror = (event) => {
      setSpeech('idle');
      if (event.error === 'not-allowed' || event.error === 'service-not-allowed') {
        setSpeechErr('Microphone access is blocked. Enable mic permission in your browser settings.');
      } else if (event.error !== 'no-speech' && event.error !== 'aborted') {
        setSpeechErr(`Dictation error: ${event.error}`);
      }
    };
    rec.onend = () => {
      setSpeech((current) => (current === 'listening' ? 'idle' : current));
      recognitionRef.current = null;
    };
    try {
      rec.start();
      recognitionRef.current = rec;
      setSpeech('listening');
    } catch (err) {
      setSpeech('idle');
      setSpeechErr(err instanceof Error ? err.message : 'Failed to start dictation');
    }
  }

  function stopDictation() {
    stopRecognition();
    setSpeech('idle');
  }

  // Hooks must run on EVERY render — this one used to sit below the
  // `if (!open) return null` and only ran once the modal opened, so the
  // hook count changed between renders and React threw ("Rendered more
  // hooks than during the previous render"). The on-site Notes tile crashed
  // to the error boundary for every tech (found 2026-09-25); `enabled`
  // keeps the focus-trap/escape wiring inert while closed.
  const dialogRef = useDialogA11y<HTMLDivElement>(onClose, open);

  const trimmed = text.trim();
  const canSave = trimmed.length > 0 && !saving;

  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Field note"
      className="fixed inset-0 z-40 bg-slate-950/40 flex items-end sm:items-center justify-center p-3"
      onClick={onClose}
    >
      <div
        ref={dialogRef}
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md rounded-xl shadow-xl p-4 space-y-3"
        style={{ backgroundColor: 'var(--color-elevated-surface)' }}
      >
        <header className="flex items-center justify-between">
          <div className="text-sm font-medium text-ink">Field note</div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="inline-flex h-7 w-7 items-center justify-center rounded-md text-ink-muted hover:bg-surface-muted"
          >
            <X className="h-4 w-4" />
          </button>
        </header>

        <div className="flex items-center gap-2">
          {speech === 'listening' ? (
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={stopDictation}
              className="bg-red-50 text-red-700 border border-red-200"
            >
              <MicOff className="h-4 w-4 mr-1.5" /> Stop dictation
            </Button>
          ) : (
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={startDictation}
              disabled={speech === 'starting'}
            >
              <Mic className="h-4 w-4 mr-1.5" />
              {speech === 'starting' ? 'Starting…' : 'Dictate'}
            </Button>
          )}
          <span className="text-[11px] text-ink-muted inline-flex items-center gap-1">
            <Type className="h-3.5 w-3.5" aria-hidden />
            or type below
          </span>
          {speech === 'listening' && (
            <span
              aria-live="polite"
              className="inline-flex items-center gap-1 text-[11px] text-red-700 ml-auto"
            >
              <span
                aria-hidden
                className="inline-block h-1.5 w-1.5 rounded-full bg-red-600 animate-pulse"
              />
              Listening
            </span>
          )}
        </div>

        <textarea
          ref={textareaRef}
          value={text}
          onChange={(e) => {
            setText(e.target.value);
            committedRef.current = e.target.value;
          }}
          rows={6}
          placeholder="What did you find? What did you fix? Anything the office needs to know."
          className={cn(
            'block w-full rounded-md border bg-surface px-3 py-2 text-sm text-ink',
            'focus:outline-none focus:ring-2 focus:ring-brand-300',
          )}
          style={{ borderColor: 'var(--color-border-secondary)' }}
        />

        {speechErr && <Feedback variant="warning" size="sm">{speechErr}</Feedback>}
        {error && <Feedback variant="error" size="sm">{error}</Feedback>}

        <footer className="flex justify-end gap-2 pt-1">
          <Button type="button" variant="ghost" size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button
            type="button"
            size="sm"
            disabled={!canSave}
            onClick={() => {
              const value = trimmed;
              if (!value) return;
              void Promise.resolve(onSave(value)).then(() => {
                /* parent closes on success */
              });
            }}
          >
            {saving ? 'Saving…' : 'Save to order'}
          </Button>
        </footer>
      </div>
    </div>
  );
}
